import { useEffect } from 'react'
import { useField, useFormikContext } from 'formik'
import * as Yup from 'yup'
import { Col, Form, InputGroup, Row } from 'react-bootstrap'
import { DirtyEffect as DirtyEffectBase } from '../CommonComponents'
import { createSimpleValidation } from '../common'
import type { HalfSizeImageCharacterSetting } from '../types'

const DirtyEffect = DirtyEffectBase as React.ComponentType<{ dirty: boolean; children: React.ReactNode }>

const CHARACTER_COUNT_VALIDATION_MESSAGE = '「画像」「空白」「記事」の合計は紙面の「字数」と一致する必要があります。'

interface Props {
  layoutable: { numberOfCharactersPerRow: number }
  isDisabled: boolean
}

interface FormValues {
  figures: Array<{ numberOfOccupyingColumns: string | number }>
  halfSizeImageCharacterSetting: HalfSizeImageCharacterSetting
}

export const HalfSizeImageCharacterSettingFields = ({ layoutable, isDisabled }: Props) => {
  const context = useFormikContext<FormValues>()

  const halfSizeExists = context.values.figures.some(
    (figure) => Number(figure.numberOfOccupyingColumns) !== Math.floor(Number(figure.numberOfOccupyingColumns))
  )
  const disabled = isDisabled || !halfSizeExists

  const validateHalfSizeImageCharacterSettings = (name: string) => (value: unknown) => {
    if (!halfSizeExists) {
      return true
    }
    const others = ['imageCharacters', 'spaceCharacters', 'textCharacters'].filter((fieldName) => name !== fieldName)
    const sumOfCharacters =
      (value as number) +
      others.reduce((acc, fieldName) => {
        return acc + (context.values.halfSizeImageCharacterSetting as unknown as Record<string, number>)[fieldName]
      }, 0)
    return sumOfCharacters === layoutable.numberOfCharactersPerRow
  }
  const validator = (name: string) =>
    createSimpleValidation(
      Yup.number()
        .required()
        .min(1)
        .test(
          'character_count_validation',
          CHARACTER_COUNT_VALIDATION_MESSAGE,
          validateHalfSizeImageCharacterSettings(name)
        )
    )

  const [imageCharactersField, imageCharactersMeta, imageCharactersHelper] = useField({
    name: 'halfSizeImageCharacterSetting.imageCharacters',
    validate: validator('imageCharacters')
  })
  const [spaceCharactersField, spaceCharactersMeta, spaceCharactersHelper] = useField({
    name: 'halfSizeImageCharacterSetting.spaceCharacters',
    validate: validator('spaceCharacters')
  })
  const [textCharactersField, textCharactersMeta, textCharactersHelper] = useField({
    name: 'halfSizeImageCharacterSetting.textCharacters',
    validate: validator('textCharacters')
  })

  useEffect(() => {
    imageCharactersHelper.setValue(imageCharactersField.value, true)
    spaceCharactersHelper.setValue(spaceCharactersField.value, true)
    textCharactersHelper.setValue(textCharactersField.value, true)

    // value は参照のみで変更検知の対象ではない
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [halfSizeExists])

  const createOnChange =
    (
      field: { onChange: React.ChangeEventHandler<HTMLElement> },
      helper1: { setTouched: (t: boolean, v?: boolean) => void },
      helper2: { setTouched: (t: boolean, v?: boolean) => void }
    ): React.ChangeEventHandler<HTMLElement> =>
    async (e) => {
      field.onChange(e)
      setTimeout(() => {
        helper1.setTouched(true, false)
        helper2.setTouched(true, false)
        context.validateForm()
      }, 0)
    }
  const onImageChange = createOnChange(imageCharactersField, spaceCharactersHelper, textCharactersHelper)
  const onSpaceChange = createOnChange(spaceCharactersField, imageCharactersHelper, textCharactersHelper)
  const onTextChange = createOnChange(textCharactersField, imageCharactersHelper, spaceCharactersHelper)

  return (
    <>
      <div className='p-1 me-2'>
        <Row className='mb-2'>
          <Col>
            <div className='fs-5'>画像半指定時の字数設定（紙面字数: {layoutable.numberOfCharactersPerRow}字）</div>
          </Col>
        </Row>
        <Row className='mb-2'>
          <Form.Group as={Col}>
            <DirtyEffect dirty={imageCharactersMeta.initialValue !== imageCharactersField.value}>
              <InputGroup size='sm' hasValidation>
                <InputGroup.Text>画像</InputGroup.Text>
                <Form.Control
                  type='number'
                  {...imageCharactersField}
                  onChange={onImageChange}
                  isInvalid={!!imageCharactersMeta.error}
                  disabled={disabled}
                />
                <InputGroup.Text>字</InputGroup.Text>
                <Form.Control.Feedback type='invalid'>{imageCharactersMeta.error}</Form.Control.Feedback>
              </InputGroup>
            </DirtyEffect>
          </Form.Group>
          <Form.Group as={Col}>
            <DirtyEffect dirty={spaceCharactersMeta.initialValue !== spaceCharactersField.value}>
              <InputGroup size='sm' hasValidation>
                <InputGroup.Text>空白</InputGroup.Text>
                <Form.Control
                  type='number'
                  {...spaceCharactersField}
                  readOnly
                  onChange={onSpaceChange}
                  isInvalid={!!spaceCharactersMeta.error}
                  disabled={disabled}
                />
                <InputGroup.Text>字</InputGroup.Text>
                <Form.Control.Feedback type='invalid'>{spaceCharactersMeta.error}</Form.Control.Feedback>
              </InputGroup>
            </DirtyEffect>
          </Form.Group>
          <Form.Group as={Col}>
            <DirtyEffect dirty={textCharactersMeta.initialValue !== textCharactersField.value}>
              <InputGroup size='sm' hasValidation>
                <InputGroup.Text>記事</InputGroup.Text>
                <Form.Control
                  type='number'
                  {...textCharactersField}
                  onChange={onTextChange}
                  isInvalid={!!textCharactersMeta.error}
                  disabled={disabled}
                />
                <InputGroup.Text>字</InputGroup.Text>
                <Form.Control.Feedback type='invalid'>{textCharactersMeta.error}</Form.Control.Feedback>
              </InputGroup>
            </DirtyEffect>
          </Form.Group>
        </Row>
      </div>
    </>
  )
}
